import { apiClient, type Paginated } from '@/core/api';
import type { Company, CompanyInput } from './types';

export const BASE = '/crm/companies/';

export interface CompanyListParams {
  page?: number;
  /** SearchFilter across name (backend ADR-016). */
  search?: string;
  is_active?: boolean;
  ordering?: string;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** Filter to rows carrying this collab tag id. */
  tag?: number;
  industry?: string;
  /** Derived from country server-side (ADR-108). */
  region?: string;
  /** ISO alpha-2, case-insensitive. */
  country?: string;
  /** Matched case-insensitively against the org's spelling. */
  technology?: string;
}

export async function listCompanies(params: CompanyListParams = {}): Promise<Paginated<Company>> {
  const { data } = await apiClient.get<Paginated<Company>>(BASE, { params });
  return data;
}

export async function getCompany(id: string | number): Promise<Company> {
  const { data } = await apiClient.get<Company>(`${BASE}${id}/`);
  return data;
}

export async function createCompany(input: CompanyInput): Promise<Company> {
  const { data } = await apiClient.post<Company>(BASE, input);
  return data;
}

export async function updateCompany(id: string | number, input: Partial<CompanyInput>): Promise<Company> {
  const { data } = await apiClient.patch<Company>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a company. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreCompany} to reverse.
 */
export async function archiveCompany(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreCompany(id: string | number): Promise<Company> {
  const { data } = await apiClient.post<Company>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived company (hard delete; admin only, irreversible). */
export async function purgeCompany(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** The org's technology tags in use — the autocomplete (ADR-108). */
export async function listCompanyTechnologies(): Promise<string[]> {
  const { data } = await apiClient.get<{ technologies: string[] }>(`${BASE}technologies/`);
  return data.technologies;
}
